/**
 * The two badges a match shows, with the picker that decides them.
 *
 * Lives inside a Manage Games expanded row. What renders here is the
 * server's own resolution (assignment first, exact catalog match second) —
 * the same helper the club-facing Games list reads — so what staff see
 * while assigning IS what the club will see.
 */
import { useCallback, useEffect, useState } from 'react'

import { ApiError, api } from '../lib/api'
import ClubLogo from './ClubLogo'
import './team-badges.css'

type TeamEntry = { code: string; url: string | null; assigned: string | null }
type CatalogEntry = { code: string; url: string }

/** Mirrors app/utils/game_code.py: 'AWAY@HOME[@YYYYMMDD]', tolerating the
 *  legacy form where a 4+ digit date was glued onto a team token. */
export function teamsOf(gameCode: string): [string, string] {
  const parts = gameCode.split('@')
  const clean = (t: string) => t.replace(/\d{4,}\w*$/, '') || t
  const away = clean(parts[0] ?? '')
  const home = clean(parts[1] ?? parts[0] ?? '')
  return [away.toUpperCase(), home.toUpperCase()]
}

// One catalog per page load: it changes only when somebody uploads to it,
// and the uploader refreshes it explicitly below.
let catalogCache: CatalogEntry[] | null = null

export default function TeamBadges({ orgId, gameCode, canUpload }: {
  orgId: number
  gameCode: string
  canUpload: boolean
}) {
  const [away, home] = teamsOf(gameCode)
  const [teams, setTeams] = useState<Record<string, TeamEntry> | null>(null)
  const [picking, setPicking] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    api<{ teams: TeamEntry[] }>(`/api/v1/admin/clubs/${orgId}/team-logos`)
      .then((body) => {
        const map: Record<string, TeamEntry> = {}
        for (const t of body.teams) map[t.code] = t
        setTeams(map)
      })
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? e.message : String(e)))
  }, [orgId])

  useEffect(load, [load])

  if (error) return <p className="muted tb-error">Badges unavailable — {error}</p>
  if (!teams) return <p className="muted">Loading badges…</p>

  const codes = away === home ? [away] : [away, home]
  return (
    <div className="tb">
      <span className="t-label">Team badges</span>
      <div className="tb-row">
        {codes.map((code) => {
          const entry = teams[code] ?? { code, url: null, assigned: null }
          return (
            <span key={code} className="tb-team">
              <ClubLogo code={code} url={entry.url} name={code} />
              <span className="tb-team__code">{code}</span>
              <span className="muted tb-team__state">
                {entry.assigned ? `assigned: ${entry.assigned}`
                  : entry.url ? 'automatic match' : 'no badge'}
              </span>
              <button className="adm-btn adm-btn--quiet"
                      onClick={() => setPicking(code)}>
                Change
              </button>
            </span>
          )
        })}
      </div>
      {picking && (
        <BadgePicker orgId={orgId} team={teams[picking]
                       ?? { code: picking, url: null, assigned: null }}
                     canUpload={canUpload}
                     onClose={() => setPicking(null)}
                     onChanged={() => { setPicking(null); load() }} />
      )}
    </div>
  )
}

function BadgePicker({ orgId, team, canUpload, onClose, onChanged }: {
  orgId: number
  team: TeamEntry
  canUpload: boolean
  onClose: () => void
  onChanged: () => void
}) {
  const [catalog, setCatalog] = useState<CatalogEntry[] | null>(catalogCache)
  const [filter, setFilter] = useState(team.code.toLowerCase())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (catalogCache) return
    api<{ logos: CatalogEntry[] }>('/api/v1/admin/team-logos')
      .then((body) => { catalogCache = body.logos; setCatalog(body.logos) })
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? e.message : String(e)))
  }, [])

  async function act(run: () => Promise<unknown>) {
    setBusy(true)
    setError(null)
    try {
      await run()
      onChanged()
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : String(e))
      setBusy(false)
    }
  }

  const assign = (logoCode: string) => act(() =>
    api(`/api/v1/admin/clubs/${orgId}/team-logos/${encodeURIComponent(team.code)}`,
        { method: 'PUT', body: JSON.stringify({ logo_code: logoCode }) }))

  const clear = () => act(() =>
    api(`/api/v1/admin/clubs/${orgId}/team-logos/${encodeURIComponent(team.code)}`,
        { method: 'DELETE' }))

  async function uploadFile(file: File) {
    setBusy(true)
    setError(null)
    try {
      const buf = new Uint8Array(await file.arrayBuffer())
      let bin = ''
      for (const b of buf) bin += String.fromCharCode(b)
      const uploaded = await api<{ code: string }>('/api/v1/admin/team-logos', {
        method: 'POST',
        body: JSON.stringify({
          code: team.code.toLowerCase(),
          data_base64: btoa(bin),
        }),
      })
      catalogCache = null // the catalog just grew; refetch next open
      // Assign what was just uploaded: uploading from this dialog says
      // "this badge, for this team", not "file it away for later".
      await api(`/api/v1/admin/clubs/${orgId}/team-logos/${encodeURIComponent(team.code)}`,
                { method: 'PUT', body: JSON.stringify({ logo_code: uploaded.code }) })
      onChanged()
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : String(e))
      setBusy(false)
    }
  }

  const q = filter.trim().toLowerCase()
  const shown = (catalog ?? []).filter((c) => !q || c.code.includes(q))

  return (
    <div className="mg-scrim" role="dialog" aria-modal="true"
         aria-label={`Badge for ${team.code}`}>
      <div className="panel mg-confirm tb-picker">
        <h3 className="mg-confirm__title">Badge for {team.code}</h3>
        <p className="adm-note">
          Applies wherever {team.code} appears in this club&rsquo;s games.
          {team.assigned
            ? ` Currently assigned: ${team.assigned}.`
            : team.url ? ' Currently matched automatically by code.'
              : ' Currently no badge.'}
        </p>

        {error && <div className="panel adm-warn">{error}</div>}

        <input className="adm-input" value={filter} autoFocus
               onChange={(e) => setFilter(e.target.value)}
               placeholder="Filter the catalog" />

        {!catalog ? <p className="muted">Loading the catalog…</p> : (
          <div className="tb-grid" role="listbox" aria-label="Logo catalog">
            {shown.map((c) => (
              <button key={c.code} className="tb-choice" disabled={busy}
                      onClick={() => void assign(c.code)}
                      title={`Assign ${c.code}`}>
                <img className="tb-choice__img" src={c.url} alt="" loading="lazy" />
                <span className="tb-choice__code">{c.code}</span>
              </button>
            ))}
            {shown.length === 0 && (
              <p className="muted">Nothing in the catalog matches.</p>
            )}
          </div>
        )}

        {canUpload && (
          <label className="tb-upload">
            <span className="t-label">
              Add a badge for {team.code} (PNG, up to 1 MB)
            </span>
            <input type="file" accept="image/png" disabled={busy}
                   onChange={(e) => {
                     const f = e.target.files?.[0]
                     if (f) void uploadFile(f)
                   }} />
          </label>
        )}

        <div className="adm-actions">
          {team.assigned && (
            <button className="adm-btn" disabled={busy}
                    onClick={() => void clear()}>
              Clear assignment (use automatic match)
            </button>
          )}
          <button className="adm-btn adm-btn--quiet" disabled={busy}
                  onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
